import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase, BUCKET_NAME } from '../lib/supabase';
import { CATEGORIES, LIST_MAPPING } from '../lib/constants';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiSave, FiArrowLeft, FiCamera, FiX, FiLoader, FiFileText, FiTrash2, FiInfo, FiPlus } = FiIcons;

const Field = ({ label, name, value, onChange, type = 'text', listKey, lists = {}, required, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{label}</label>
    {listKey ? (
      <select
        name={name}
        value={value ?? ''}
        onChange={onChange}
        required={required}
        className="border border-slate-200 dark:border-slate-700 rounded-md p-2 text-sm focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white w-full outline-none transition-all"
      >
        <option value="">- Wählen -</option>
        {(lists[listKey] || []).map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : type === 'textarea' ? (
      <textarea
        name={name}
        value={value ?? ''}
        onChange={onChange}
        rows={3}
        className="border border-slate-200 dark:border-slate-700 rounded-md p-2 text-sm focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white w-full outline-none"
        {...props}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value ?? ''}
        onChange={onChange}
        required={required}
        {...props}
        className="border border-slate-200 dark:border-slate-700 rounded-md p-2 text-sm focus:ring-2 focus:ring-yellow-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white w-full outline-none"
      />
    )}
  </div>
);

const InventoryForm = () => {
  const { category, id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const config = CATEGORIES[category];
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lists, setLists] = useState({});
  const [formData, setFormData] = useState({
    category: category,
    inventory_id: '',
    manufacturer: '',
    article_number: '',
    name: '',
    condition: '',
    location: '',
    epoch: '',
    railway: '',
    system: '',
    digital_type: '',
    date_buy: '',
    count: 1,
    price_buy: 0,
    price_catalog: 0,
    note: '',
    images: [],
    pdfs: [],
    specs: {},
    year: '',
    sound: 'Nein',
    light: '',
    interior_light: '',
    coupling: '',
    transformer: '',
    lok_type: '',
    type: '',
    track_system: '',
    track_type: '',
    length: '',
    radius: '',
    width: '',
    height: '',
    diameter: '',
    digital_controllable: 'Nein',
    decoder_integrated: 'Nein',
    decoder_type: '',
    drive: '',
    illuminated: 'Nein',
    turnout_type: '',
    signal_type: '',
    signal_function: '',
    signal_aspects: '',
    prototype: '',
    with_base: 'ohne Sockel',
    usage_context: '',
    acc_type: '',
    bridge_part: '',
    level_crossing: '',
    category_ersatz: '',
    decoder_types_detail: '',
    protocols_detail: '',
    interfaces_detail: '',
    desc_product: '',
    protocol_central: '',
    booster_type: '',
    status_led: 'Nein',
    kit_manufacturer: '',
    landscape_manufacturer: '',
    figure_manufacturer: '',
    tool_manufacturer: '',
    spare_part_type: ''
  });

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      if (!category || !CATEGORIES[category]) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data: listData } = await supabase.from('app_lists').select('*');
        if (listData && isMounted) {
          const listMap = {};
          listData.forEach(l => listMap[l.key] = l.items);
          setLists(listMap);
        }

        if (!isNew) {
          const { data, error } = await supabase
            .from('inventory')
            .select('*')
            .eq('id', id)
            .single();

          if (error) throw error;
          if (data && isMounted) {
            setFormData({
              ...data,
              ...(data.specs || {}),
              images: data.images || [],
              pdfs: data.pdfs || [],
              date_buy: data.date_buy || ''
            });
          }
        } else if (isMounted) {
          const { count: existingCount } = await supabase
            .from('inventory')
            .select('id', { count: 'exact' })
            .eq('category', category);

          const nextId = `${CATEGORIES[category].prefix || 'INV-'}${String((existingCount || 0) + 1).padStart(4, '0')}`;
          setFormData(prev => ({ ...prev, inventory_id: nextId }));
        }
      } catch (err) {
        console.error("Initialization error:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    init();
    return () => { isMounted = false; };
  }, [id, category, isNew]);

  const handleChange = useCallback((e) => {
    const { name, value, type } = e.target;
    let finalValue = value;
    if (type === 'number') finalValue = value === '' ? 0 : parseFloat(value);
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  }, []);

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    setSaving(true);
    try {
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${category}/${type}/${Date.now()}-${sanitizedName}`;
      const { error: uploadError } = await supabase.storage
        .from(BUCKET_NAME)
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(fileName);

      setFormData(prev => ({
        ...prev,
        [type]: [...(prev[type] || []), publicUrl]
      }));
    } catch (err) {
      alert("Upload fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const dbColumns = [
      'category', 'inventory_id', 'manufacturer', 'article_number', 'name', 'condition', 'location', 'count', 'price_buy', 'price_catalog', 'note', 'images', 'pdfs', 'epoch', 'railway', 'system', 'digital_type', 'date_buy'
    ];

    const specFields = [
      'year', 'sound', 'light', 'interior_light', 'coupling', 'drive', 'type', 'track_system', 'track_type', 'length', 'radius', 'width', 'height', 'diameter', 'turnout_type', 'signal_type', 'signal_function', 'signal_aspects', 'prototype', 'with_base', 'category_acc', 'transformer', 'lok_type', 'digital_controllable', 'decoder_integrated', 'decoder_type', 'illuminated', 'usage_context', 'acc_type', 'bridge_part', 'level_crossing', 'category_ersatz', 'decoder_types_detail', 'protocols_detail', 'interfaces_detail', 'desc_product', 'protocol_central', 'booster_type', 'status_led', 'kit_manufacturer', 'landscape_manufacturer', 'figure_manufacturer', 'tool_manufacturer', 'spare_part_type'
    ];

    const payload = {};
    const specs = {};

    Object.keys(formData).forEach(key => {
      if (dbColumns.includes(key)) {
        payload[key] = formData[key];
      } else if (specFields.includes(key)) {
        specs[key] = formData[key];
      }
    });

    payload.specs = specs;

    try {
      let error;
      if (isNew) {
        const { error: insertError } = await supabase.from('inventory').insert([payload]);
        error = insertError;
      } else {
        const { error: updateError } = await supabase.from('inventory').update(payload).eq('id', id);
        error = updateError;
      }

      if (error) throw error;
      navigate(`/inventory/${category}`);
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center p-20 text-slate-400">
      <SafeIcon icon={FiLoader} className="text-4xl animate-spin mb-4 text-yellow-500" />
      <p className="font-bold uppercase text-xs tracking-widest">Daten werden geladen...</p>
    </div>
  );

  const isAccessory = category === 'zubehoer';
  const isErsatz = category === 'zubehoer_ersatz';
  const isTracksOrTurnouts = category === 'gleise' || category === 'weichen';
  const isSignals = category === 'signale';
  const isWaggons = category === 'waggons';
  const isLoks = category === 'loks';

  return (
    <div className="max-w-5xl mx-auto pb-24 px-4 sm:px-0">
      <div className="flex items-center justify-between mb-8">
        <button type="button" onClick={() => navigate(-1)} className="flex items-center text-slate-500 hover:text-slate-800 font-bold transition-colors">
          <SafeIcon icon={FiArrowLeft} className="mr-2" /> Zurück
        </button>
        <div className="text-right">
          <h2 className="text-xl sm:text-2xl font-black text-slate-800 dark:text-white tracking-tight">
            {isNew ? 'Neuzugang' : 'Bearbeiten'}
          </h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{config.label}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 space-y-6">
              <div className="flex items-center gap-2 border-b dark:border-slate-800 pb-2 mb-4">
                <div className={`w-3 h-3 rounded-full ${config.color.split(' ')[0]}`}></div>
                <h3 className="font-bold text-slate-800 dark:text-white uppercase text-xs tracking-widest">Stammdaten</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Inventar-ID" name="inventory_id" value={formData.inventory_id} onChange={handleChange} required />
                <Field label="Hersteller" name="manufacturer" value={formData.manufacturer} onChange={handleChange} listKey={LIST_MAPPING.manufacturer} lists={lists} />
                <Field label="Artikelnummer" name="article_number" value={formData.article_number} onChange={handleChange} />
                <Field label="Bezeichnung" name="name" value={formData.name} onChange={handleChange} />
                
                {/* Bahnverwaltung nach Bezeichnung */}
                {!isTracksOrTurnouts && !isErsatz && (
                  <Field label="Bahnverwaltung" name="railway" value={formData.railway} onChange={handleChange} listKey={LIST_MAPPING.railway} lists={lists} />
                )}

                <Field label="Gleissystem" name="track_system" value={formData.track_system} onChange={handleChange} listKey={LIST_MAPPING.track_system} lists={lists} />
                
                {isLoks && (
                  <Field label="Lok-Typ" name="lok_type" value={formData.lok_type} onChange={handleChange} listKey={LIST_MAPPING.lok_type} lists={lists} />
                )}

                <Field label="Zustand" name="condition" value={formData.condition} onChange={handleChange} listKey={LIST_MAPPING.condition} lists={lists} />
                <Field label="Kaufdatum" name="date_buy" value={formData.date_buy} onChange={handleChange} type="date" />
                <Field label="Standort" name="location" value={formData.location} onChange={handleChange} listKey={LIST_MAPPING.location} lists={lists} />
              </div>

              <div className="flex items-center gap-2 border-b dark:border-slate-800 pb-2 pt-4 mb-4">
                <SafeIcon icon={FiInfo} className="text-yellow-500" />
                <h3 className="font-bold text-slate-800 dark:text-white uppercase text-xs tracking-widest">Details</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isWaggons && (
                  <>
                    <Field label="Waggon-Typ" name="type" value={formData.type} onChange={handleChange} listKey={LIST_MAPPING.type} lists={lists} />
                    <Field label="Epoche" name="epoch" value={formData.epoch} onChange={handleChange} listKey={LIST_MAPPING.epoch} lists={lists} />
                    <Field label="Kupplung" name="coupling" value={formData.coupling} onChange={handleChange} listKey={LIST_MAPPING.coupling} lists={lists} />
                    <Field label="Innenbeleuchtung" name="interior_light" value={formData.interior_light} onChange={handleChange} listKey={LIST_MAPPING.interior_light} lists={lists} />
                  </>
                )}

                {isTracksOrTurnouts && (
                  <>
                    {category === 'gleise' && (
                      <Field label="Gleis-Typ" name="track_type" value={formData.track_type} onChange={handleChange} listKey={LIST_MAPPING.track_type} lists={lists} />
                    )}
                    {category === 'weichen' && (
                      <Field label="Weichen-Typ" name="turnout_type" value={formData.turnout_type} onChange={handleChange} listKey={LIST_MAPPING.turnout_type} lists={lists} />
                    )}
                    <Field label="Länge in mm" name="length" value={formData.length} onChange={handleChange} type="number" />
                    <Field label="Radius" name="radius" value={formData.radius} onChange={handleChange} />
                    {category === 'weichen' && (
                      <>
                        <Field label="Antrieb" name="drive" value={formData.drive} onChange={handleChange} listKey={LIST_MAPPING.drive} lists={lists} />
                        <Field label="Digital steuerbar" name="digital_controllable" value={formData.digital_controllable} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                        <Field label="Decoder integriert" name="decoder_integrated" value={formData.decoder_integrated} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                        <Field label="Beleuchtet" name="illuminated" value={formData.illuminated} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                      </>
                    )}
                  </>
                )}

                {isSignals && (
                  <>
                    <Field label="Signal Bauart" name="signal_type" value={formData.signal_type} onChange={handleChange} listKey={LIST_MAPPING.signal_type} lists={lists} />
                    <Field label="Signal Funktion" name="signal_function" value={formData.signal_function} onChange={handleChange} listKey={LIST_MAPPING.signal_function} lists={lists} />
                    <Field label="Signal Bilder" name="signal_aspects" value={formData.signal_aspects} onChange={handleChange} listKey={LIST_MAPPING.signal_aspects} lists={lists} />
                    <Field label="Vorbild" name="prototype" value={formData.prototype} onChange={handleChange} listKey={LIST_MAPPING.prototypes} lists={lists} />
                    <Field label="Länge in mm" name="length" value={formData.length} onChange={handleChange} type="number" />
                    <Field label="Breite in mm" name="width" value={formData.width} onChange={handleChange} type="number" />
                    <Field label="Höhe in mm" name="height" value={formData.height} onChange={handleChange} type="number" />
                    <Field label="mit Sockel" name="with_base" value={formData.with_base} onChange={handleChange} listKey={LIST_MAPPING.base_option} lists={lists} />
                    <Field label="Digital steuerbar" name="digital_controllable" value={formData.digital_controllable} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                    <Field label="Decoder integriert" name="decoder_integrated" value={formData.decoder_integrated} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                    <Field label="Decoderart" name="decoder_type" value={formData.decoder_type} onChange={handleChange} listKey={LIST_MAPPING.decoder_type} lists={lists} />
                    <Field label="Einsatz" name="usage_context" value={formData.usage_context} onChange={handleChange} />
                  </>
                )}

                {isAccessory && (
                  <>
                    <Field label="Zubehör-Art" name="acc_type" value={formData.acc_type} onChange={handleChange} listKey={LIST_MAPPING.acc_type} lists={lists} />
                    {formData.acc_type === 'Brückenteil' && (
                      <Field label="Brückenteil" name="bridge_part" value={formData.bridge_part} onChange={handleChange} listKey={LIST_MAPPING.bridge_part} lists={lists} />
                    )}
                    {formData.acc_type === 'Transformator' && (
                      <Field label="Transformator" name="transformer" value={formData.transformer} onChange={handleChange} listKey={LIST_MAPPING.transformer} lists={lists} />
                    )}
                    {formData.acc_type === 'Bahnübergang' && (
                      <Field label="Bahnübergang" name="level_crossing" value={formData.level_crossing} onChange={handleChange} listKey={LIST_MAPPING.level_crossing} lists={lists} />
                    )}
                    <Field label="Vorbild" name="prototype" value={formData.prototype} onChange={handleChange} listKey={LIST_MAPPING.prototype_acc} lists={lists} />
                    <Field label="Länge in mm" name="length" value={formData.length} onChange={handleChange} type="number" />
                    <Field label="Breite in mm" name="width" value={formData.width} onChange={handleChange} type="number" />
                    <Field label="Höhe in mm" name="height" value={formData.height} onChange={handleChange} type="number" />
                    <Field label="Durchmesser mm" name="diameter" value={formData.diameter} onChange={handleChange} type="number" />
                    <Field label="mit Sockel" name="with_base" value={formData.with_base} onChange={handleChange} listKey={LIST_MAPPING.base_option} lists={lists} />
                    <Field label="Digital steuerbar" name="digital_controllable" value={formData.digital_controllable} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                    <Field label="Decoder integriert" name="decoder_integrated" value={formData.decoder_integrated} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                    <Field label="Decoderart" name="decoder_type" value={formData.decoder_type} onChange={handleChange} listKey={LIST_MAPPING.decoder_type} lists={lists} />
                    <Field label="Einsatz" name="usage_context" value={formData.usage_context} onChange={handleChange} />
                  </>
                )}

                {isErsatz && (
                  <>
                    <Field label="Zubehör-Kategorie" name="category_ersatz" value={formData.category_ersatz} onChange={handleChange} listKey={LIST_MAPPING.category_ersatz} lists={lists} required />
                    
                    {formData.category_ersatz === 'Decoder' && (
                      <>
                        <Field label="Decoder Typ" name="decoder_types_detail" value={formData.decoder_types_detail} onChange={handleChange} listKey={LIST_MAPPING.decoder_types_detail} lists={lists} />
                        <Field label="Protokolle" name="protocols_detail" value={formData.protocols_detail} onChange={handleChange} listKey={LIST_MAPPING.protocols_detail} lists={lists} />
                        <Field label="Schnittstelle" name="interfaces_detail" value={formData.interfaces_detail} onChange={handleChange} listKey={LIST_MAPPING.interfaces_detail} lists={lists} />
                      </>
                    )}

                    {formData.category_ersatz === 'Zentrale' && (
                      <Field label="Zentralen-Protokoll" name="protocol_central" value={formData.protocol_central} onChange={handleChange} listKey={LIST_MAPPING.central_protocols} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Booster' && (
                      <Field label="Booster-Typ" name="booster_type" value={formData.booster_type} onChange={handleChange} listKey={LIST_MAPPING.booster_types} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Trafo Netzteil' && (
                      <Field label="Transformator" name="transformer" value={formData.transformer} onChange={handleChange} listKey={LIST_MAPPING.transformer} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Elektrik' && (
                      <Field label="Status LED" name="status_led" value={formData.status_led} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Gebäude' && (
                      <Field label="Bausatz-Hersteller" name="kit_manufacturer" value={formData.kit_manufacturer} onChange={handleChange} listKey={LIST_MAPPING.kit_manufacturer} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Landschaft' && (
                      <Field label="Landschaft-Marke" name="landscape_manufacturer" value={formData.landscape_manufacturer} onChange={handleChange} listKey={LIST_MAPPING.landscape_manufacturers} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Figuren' && (
                      <Field label="Figuren-Marke" name="figure_manufacturer" value={formData.figure_manufacturer} onChange={handleChange} listKey={LIST_MAPPING.figure_manufacturers} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Werkzeug' && (
                      <Field label="Werkzeug-Hersteller" name="tool_manufacturer" value={formData.tool_manufacturer} onChange={handleChange} listKey={LIST_MAPPING.tool_manufacturers} lists={lists} />
                    )}

                    {formData.category_ersatz === 'Ersatzteile' && (
                      <Field label="Ersatzteil-Art" name="spare_part_type" value={formData.spare_part_type} onChange={handleChange} listKey={LIST_MAPPING.spare_part_types} lists={lists} />
                    )}
                    
                    <Field label="Produktbeschreibung" name="desc_product" value={formData.desc_product} onChange={handleChange} type="textarea" />
                  </>
                )}

                {!isTracksOrTurnouts && !isSignals && !isWaggons && !isAccessory && !isErsatz && (
                  <>
                    <Field label="Epoche" name="epoch" value={formData.epoch} onChange={handleChange} listKey={LIST_MAPPING.epoch} lists={lists} />
                  </>
                )}

                {!isErsatz && (
                  <>
                    <Field label="System" name="system" value={formData.system} onChange={handleChange} listKey={LIST_MAPPING.system} lists={lists} />
                    <Field label="Digital-Typ" name="digital_type" value={formData.digital_type} onChange={handleChange} listKey={LIST_MAPPING.digital_type} lists={lists} />
                  </>
                )}

                {(category === 'loks' || category === 'sets' || category === 'waggons') && (
                  <>
                    <Field label="Baujahr" name="year" value={formData.year} onChange={handleChange} />
                    <Field label="Sound" name="sound" value={formData.sound} onChange={handleChange} listKey={LIST_MAPPING.bool} lists={lists} />
                  </>
                )}
              </div>
              <Field label="Notizen" name="note" value={formData.note} onChange={handleChange} type="textarea" />
            </div>

            {/* Dokumente */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
              <h3 className="font-bold text-slate-800 dark:text-white text-xs uppercase mb-4 flex items-center gap-2 tracking-widest">
                <SafeIcon icon={FiFileText} /> Dokumente (PDF, max. 4)
              </h3>
              <div className="space-y-3">
                {formData.pdfs?.map((pdf, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl border dark:border-slate-700">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <SafeIcon icon={FiFileText} className="text-red-500 shrink-0" />
                      <span className="text-xs font-bold text-slate-600 dark:text-slate-300 truncate font-mono">DOC_{idx + 1}</span>
                    </div>
                    <button type="button" onClick={() => setFormData(p => ({ ...p, pdfs: p.pdfs.filter((_, i) => i !== idx) }))} className="p-2 text-slate-400 hover:text-red-500 transition-colors">
                      <SafeIcon icon={FiTrash2} size={14} />
                    </button>
                  </div>
                ))}
                {(formData.pdfs?.length || 0) < 4 && (
                  <label className="flex items-center justify-center gap-3 p-4 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl cursor-pointer hover:border-yellow-500 transition-all text-slate-400 hover:text-yellow-600 bg-slate-50/50 dark:bg-slate-800/30">
                    <SafeIcon icon={FiPlus} />
                    <span className="text-xs font-black uppercase">PDF hinzufügen</span>
                    <input type="file" className="hidden" accept="application/pdf" onChange={(e) => handleFileUpload(e, 'pdfs')} disabled={saving} />
                  </label>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-900 dark:bg-slate-950 text-white p-6 rounded-2xl shadow-xl space-y-4">
              <h3 className="font-bold text-yellow-500 uppercase text-[10px] tracking-widest">Werte</h3>
              <Field label="Anzahl" name="count" value={formData.count} onChange={handleChange} type="number" required />
              <Field label="Kaufpreis (€)" name="price_buy" value={formData.price_buy} onChange={handleChange} type="number" step="0.01" />
              <Field label="Katalogwert (€)" name="price_catalog" value={formData.price_catalog} onChange={handleChange} type="number" step="0.01" />
              <div className="pt-4 border-t border-slate-800 text-right">
                <div className="text-[10px] text-slate-500 font-bold uppercase">Gesamtwert</div>
                <div className="text-3xl font-black text-yellow-400">
                  {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(formData.count * (formData.price_catalog || formData.price_buy || 0))}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800">
              <h3 className="font-bold text-slate-800 dark:text-white text-xs uppercase mb-4 tracking-widest">Fotos (max. 4MB)</h3>
              <div className="grid grid-cols-2 gap-2">
                {formData.images?.map((img, idx) => (
                  <div key={idx} className="relative aspect-square rounded-lg overflow-hidden border dark:border-slate-700">
                    <img src={img} className="w-full h-full object-cover" alt="Upload" />
                    <button type="button" onClick={() => setFormData(p => ({ ...p, images: p.images.filter((_, i) => i !== idx) }))} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full shadow-lg">
                      <SafeIcon icon={FiX} size={10} />
                    </button>
                  </div>
                ))}
                <label className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-slate-400 hover:text-yellow-600 cursor-pointer bg-slate-50 dark:bg-slate-800/50">
                  <SafeIcon icon={FiCamera} size={20} />
                  <input type="file" className="hidden" onChange={(e) => {
                    const file = e.target.files[0];
                    if (file && file.size > 4 * 1024 * 1024) {
                      alert("Bild ist zu groß (max. 4MB)");
                      return;
                    }
                    handleFileUpload(e, 'images');
                  }} accept="image/png, image/jpeg, image/webp" disabled={saving} />
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="fixed bottom-6 right-6 z-40">
          <button type="submit" disabled={saving} className="flex items-center gap-3 bg-yellow-500 hover:bg-yellow-600 text-slate-900 px-8 py-4 rounded-full font-black shadow-2xl transition-all transform active:scale-95 disabled:bg-slate-300">
            {saving ? <SafeIcon icon={FiLoader} className="animate-spin" /> : <SafeIcon icon={FiSave} />}
            {isNew ? 'ANLEGEN' : 'SPEICHERN'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default InventoryForm;